import {
  Button,
  Checkbox,
  Flex,
  Image,
  Input,
  LoadingOverlay,
  Table,
  Tabs,
  TextInput,
  Textarea,
  Title,
  rem,
  Pagination,
  Text,
  Select,
  Tooltip,
} from "@mantine/core";
import { assets, colors, BatchRuleConditionType } from "../../constants";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ROUTES } from "../../constants/routes";
import { useAppData } from "../../contexts/AppContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useAuth } from "../../hooks/useAuth";
import moment from "moment";
import { highlightedText } from "../../modules/highlightedText";
import { db } from "../../firebase";
import { collection, Timestamp, addDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import { addSubcollectionDoc } from "../../repositories/firebase";
import { COLLECTIONS, formatPhoneNumber } from "../../constants/global";
import Papa from "papaparse";
import {
  getPharmetikaPendingOrders,
  invalidatePharmetikaPendingOrdersCache,
} from "../../repositories/pharmetikaPendingOrders";

const sendSms = ({ uid, oid, rxNumber, drName, phone, name, sender }) => {
  const BASE_URL =
    process.env.REACT_APP_ENV === "prod"
      ? "https://rx2text.com/"
      : "http://localhost:3000";

  let params = `oid=${oid}&rxNumber=${rxNumber}`;
  let link = `${BASE_URL}?${params}`;
  console.log("SMS link:", link);
  const cloudFunctionUrl =
    "https://us-central1-art-of-med-text-to-order.cloudfunctions.net/app/sendWhippySMS";

  let message = `Hey ${name}! This is The Art of Medicine Compounding Pharmacy. It looks like ${drName} prescribed you a medication. For easy and secure ordering, click here: ${link}`;
  axios
    .post(cloudFunctionUrl, {
      data: {
        phoneNumber: phone,
        message: message,
      },
    })
    .then((response) => {
      console.info("SMS sent successfully", response.data);
      addSubcollectionDoc(
        COLLECTIONS.ORDERS,
        oid,
        "order-logs",
        {
          title: `Initial Message.`,
          description: `Message sent to ${name} to complete their order.`,
          sentBy: sender,
        },
        {
          createdAt: true,
        }
      );
    })
    .catch((error: any) => {
      console.error("Error sending SMS:", error);
    });
};

function ProcessPending() {
  const navigate = useNavigate();
  const [pendingLoading, setPendingLoading] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [archivedOrders, setArchivedOrders] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [modifiedRows, setModifiedRows] = useState<Set<string>>(new Set());
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [originalOrderData, setOriginalOrderData] = useState<Map<string, any>>(new Map());
  const [activeTab, setActiveTab] = useState<string | null>("pending");
  const [pendingPage, setPendingPage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(() => {
    const savedPageSize = localStorage.getItem("processPendingPageSize");
    return savedPageSize || "50";
  });
  const [archivedPageSize, setArchivedPageSize] = useState(() => {
    const savedPageSize = localStorage.getItem("processArchivedPageSize");
    return savedPageSize || "50";
  });
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [totalArchivedCount, setTotalArchivedCount] = useState(0);
  const { sidebarCollapsed, toggleSidebar } = useSidebar();
  const { user } = useAuth();

  const {
    patientsData: { savePatient },
    ordersData: { saveOrder },
    batchRulesData: { batchRules },
    updatePendingOrdersCount,
    adminsData,
  } = useAppData();

  const currentAdmin = adminsData.admins.find((admin) => admin.email === user?.email);
  const senderInfo = {
    userId: user?.uid || "",
    email: user?.email || "",
    name: currentAdmin?.name || user?.displayName || user?.email || "Unknown",
    source: "admin-ui",
  };

  // Fetch pending orders once; search and pagination work from local state.
  useEffect(() => {
    fetchPendingOrders();
  }, []);

  // Only fetch archived orders when user switches to archived tab
  useEffect(() => {
    if (activeTab === "archived" && archivedOrders.length === 0) {
      fetchArchivedOrders();
    }
  }, [activeTab, archivedOrders.length]);

  // Save page sizes to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("processPendingPageSize", pendingPageSize);
  }, [pendingPageSize]);

  useEffect(() => {
    localStorage.setItem("processArchivedPageSize", archivedPageSize);
  }, [archivedPageSize]);

  // Reset to page 1 when search text changes
  useEffect(() => {
    setPendingPage(1);
    setArchivedPage(1);
  }, [searchText]);

  // Reset to page 1 when changing tabs
  useEffect(() => {
    if (activeTab === "pending") {
      setPendingPage(1);
    } else {
      setArchivedPage(1);
    }
  }, [activeTab]);

  const fetchPendingOrders = async () => {
    setPendingLoading(true);
    try {
      const { pendingOrders: loadedPendingOrders, archivedOrders: loadedArchivedOrders } =
        await getPharmetikaPendingOrders();

      setTotalPendingCount(loadedPendingOrders.length);
      setTotalArchivedCount(loadedArchivedOrders.length);
      setPendingOrders(loadedPendingOrders);
      setArchivedOrders(loadedArchivedOrders);
    } catch (error) {
      console.error("Error fetching pending orders:", error);
      notifications.show({
        title: "Error",
        message: "Failed to fetch pending orders",
        color: "red",
      });
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchArchivedOrders = async () => {
    setArchivedLoading(true);
    try {
      const { archivedOrders: loadedArchivedOrders } = await getPharmetikaPendingOrders();

      setTotalArchivedCount(loadedArchivedOrders.length);
      setArchivedOrders(loadedArchivedOrders);
    } catch (error) {
      console.error("Error fetching archived orders:", error);
      notifications.show({
        title: "Error",
        message: "Failed to fetch archived orders",
        color: "red",
      });
    } finally {
      setArchivedLoading(false);
    }
  };

  const handleArchive = async (pendingOrdersId: string, rxNumber: string) => {
    try {
      // Get the document
      const docRef = doc(db, "pharmetika_pending_orders", pendingOrdersId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Document not found");
      }

      const data = docSnap.data();
      const orders = data.orders || [];

      // Find and mark the specific order as archived
      const updatedOrders = orders.map((order: any) => {
        if (order.rxNumber === rxNumber) {
          return { ...order, archived: true };
        }
        return order;
      });

      // Update the document with the modified orders array
      await updateDoc(docRef, {
        orders: updatedOrders,
      });
      invalidatePharmetikaPendingOrdersCache();

      // Find the archived order to add to archived list
      const archivedOrder = pendingOrders.find((order) => order.rxNumber === rxNumber);

      // Remove the order from pending orders state
      setPendingOrders((prev) =>
        prev.filter((order) => order.rxNumber !== rxNumber)
      );
      setTotalPendingCount((prev) => Math.max(prev - 1, 0));

      // Add to archived orders state
      if (archivedOrder) {
        setArchivedOrders((prev) => [{ ...archivedOrder, archived: true }, ...prev]);
        setTotalArchivedCount((prev) => prev + 1);
      }

      // Remove from selected rows if it was selected
      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rxNumber);
        return newSet;
      });

      // Remove from modified rows if it was modified
      setModifiedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rxNumber);
        return newSet;
      });

      notifications.show({
        title: "Success",
        message: "Order archived successfully",
        color: "green",
      });

      // Update the pending orders count in the sidebar
      updatePendingOrdersCount();
    } catch (error) {
      console.error("Error archiving order:", error);
      notifications.show({
        title: "Error",
        message: "Failed to archive order",
        color: "red",
      });
    }
  };

  const handleBulkArchive = async () => {
    const selectedOrders = pendingOrders.filter((order) =>
      selectedRows.has(order.rxNumber)
    );

    if (selectedOrders.length === 0) {
      notifications.show({
        title: "No orders selected",
        message: "Please select at least one order to archive",
        color: "orange",
      });
      return;
    }

    setProcessing(true);
    try {
      // Group orders by pendingOrdersId for efficient batch updates
      const ordersByDocId = new Map<string, string[]>();
      selectedOrders.forEach((order) => {
        if (!ordersByDocId.has(order.pendingOrdersId)) {
          ordersByDocId.set(order.pendingOrdersId, []);
        }
        ordersByDocId.get(order.pendingOrdersId)!.push(order.rxNumber);
      });

      // Update each document
      for (const [pendingOrdersId, rxNumbers] of Array.from(ordersByDocId.entries())) {
        const docRef = doc(db, "pharmetika_pending_orders", pendingOrdersId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          console.error(`Document ${pendingOrdersId} not found`);
          continue;
        }

        const data = docSnap.data();
        const orders = data.orders || [];

        // Mark all selected orders in this document as archived
        const updatedOrders = orders.map((order: any) => {
          if (rxNumbers.includes(order.rxNumber)) {
            return { ...order, archived: true };
          }
          return order;
        });

        await updateDoc(docRef, {
          orders: updatedOrders,
        });
      }
      invalidatePharmetikaPendingOrdersCache();

      // Update state: move selected orders to archived
      const archivedOrdersList = selectedOrders.map((order) => ({
        ...order,
        archived: true,
      }));

      setPendingOrders((prev) =>
        prev.filter((order) => !selectedRows.has(order.rxNumber))
      );
      setTotalPendingCount((prev) => Math.max(prev - selectedOrders.length, 0));

      setArchivedOrders((prev) => [...archivedOrdersList, ...prev]);
      setTotalArchivedCount((prev) => prev + archivedOrdersList.length);

      // Clear selection
      setSelectedRows(new Set());

      // Clear modified rows for archived items
      setModifiedRows((prev) => {
        const newSet = new Set(prev);
        selectedOrders.forEach((order) => newSet.delete(order.rxNumber));
        return newSet;
      });

      notifications.show({
        title: "Success",
        message: `${selectedOrders.length} order(s) archived successfully`,
        color: "green",
      });

      // Update the pending orders count in the sidebar
      updatePendingOrdersCount();
    } catch (error) {
      console.error("Error bulk archiving orders:", error);
      notifications.show({
        title: "Error",
        message: "Failed to archive some orders",
        color: "red",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleUnarchive = async (pendingOrdersId: string, rxNumber: string) => {
    try {
      // Get the document
      const docRef = doc(db, "pharmetika_pending_orders", pendingOrdersId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Document not found");
      }

      const data = docSnap.data();
      const orders = data.orders || [];

      // Find and remove the archived flag from the specific order
      const updatedOrders = orders.map((order: any) => {
        if (order.rxNumber === rxNumber) {
          const { archived, ...orderWithoutArchived } = order;
          return orderWithoutArchived;
        }
        return order;
      });

      // Update the document with the modified orders array
      await updateDoc(docRef, {
        orders: updatedOrders,
      });
      invalidatePharmetikaPendingOrdersCache();

      // Find the unarchived order to add to pending list
      const unarchivedOrder = archivedOrders.find((order) => order.rxNumber === rxNumber);

      // Remove the order from archived orders state
      setArchivedOrders((prev) =>
        prev.filter((order) => order.rxNumber !== rxNumber)
      );
      setTotalArchivedCount((prev) => Math.max(prev - 1, 0));

      // Add to pending orders state
      if (unarchivedOrder) {
        const { archived, ...orderWithoutArchived } = unarchivedOrder;
        setPendingOrders((prev) => [orderWithoutArchived, ...prev]);
        setTotalPendingCount((prev) => prev + 1);
      }

      // Remove from selected rows if it was selected
      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rxNumber);
        return newSet;
      });

      notifications.show({
        title: "Success",
        message: "Order unarchived successfully",
        color: "green",
      });

      // Update the pending orders count in the sidebar
      updatePendingOrdersCount();
    } catch (error) {
      console.error("Error unarchiving order:", error);
      notifications.show({
        title: "Error",
        message: "Failed to unarchive order",
        color: "red",
      });
    }
  };

  const handleValueChange = (e: any, rxNumber: string) => {
    setPendingOrders((prev) => {
      return prev.map((order) => {
        if (order.rxNumber === rxNumber) {
          return {
            ...order,
            [e.target.name]: e.target.value,
          };
        }
        return order;
      });
    });

    // Mark this row as modified
    setModifiedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(rxNumber);
      return newSet;
    });
  };

  const handleSaveRow = async (pendingOrdersId: string, rxNumber: string) => {
    try {
      // Find the modified order in state
      const modifiedOrder = pendingOrders.find((order) => order.rxNumber === rxNumber);
      if (!modifiedOrder) {
        throw new Error("Order not found");
      }

      // Get the document to fetch the original order data
      const docRef = doc(db, "pharmetika_pending_orders", pendingOrdersId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Document not found");
      }

      const data = docSnap.data();
      const orders = data.orders || [];

      // Find the original order before changes
      const originalOrder = orders.find((order: any) => order.rxNumber === rxNumber);
      if (!originalOrder) {
        throw new Error("Original order not found");
      }

      // Track changes
      const changes: any[] = [];
      const fieldsToTrack = [
        'firstName', 'lastName', 'species', 'dob', 'cell',
        'drFirstName', 'drLastName', 'medication', 'form',
        'qty', 'price', 'daySupply', 'authRefills', 'memo', 'rxNumber'
      ];

      fieldsToTrack.forEach((field) => {
        const oldValue = originalOrder[field];
        const newValue = modifiedOrder[field];
        
        // Check if value actually changed
        if (oldValue !== newValue) {
          changes.push({
            field,
            oldValue: oldValue || '',
            newValue: newValue || ''
          });
        }
      });

      // Only proceed if there are actual changes
      if (changes.length > 0) {
        // Update the specific order with modified values
        const updatedOrders = orders.map((order: any) => {
          if (order.rxNumber === rxNumber) {
            return {
              ...order,
              firstName: modifiedOrder.firstName,
              lastName: modifiedOrder.lastName,
              species: modifiedOrder.species,
              dob: modifiedOrder.dob,
              cell: modifiedOrder.cell,
              drFirstName: modifiedOrder.drFirstName,
              drLastName: modifiedOrder.drLastName,
              medication: modifiedOrder.medication,
              form: modifiedOrder.form,
              qty: modifiedOrder.qty,
              price: modifiedOrder.price,
              daySupply: modifiedOrder.daySupply,
              authRefills: modifiedOrder.authRefills,
              memo: modifiedOrder.memo,
              rxNumber: modifiedOrder.rxNumber,
            };
          }
          return order;
        });

        // Update the document
        await updateDoc(docRef, {
          orders: updatedOrders,
        });
        invalidatePharmetikaPendingOrdersCache();

        // Create change log in new collection
        await addDoc(collection(db, COLLECTIONS.PENDING_ORDER_CHANGE_LOGS), {
          pendingOrdersId: pendingOrdersId,
          rxNumber: rxNumber,
          webhookId: data.webhookId || '',
          patientName: `${modifiedOrder.firstName} ${modifiedOrder.lastName}`,
          changes: changes,
          changedAt: Timestamp.now(),
          changeCount: changes.length,
          changedBy: user?.email || '',
          changedByUid: user?.uid || '',
        });

        console.log('Change log created:', {
          rxNumber,
          changesCount: changes.length,
          changes
        });
      }

      // Remove from modified rows and exit edit mode
      setModifiedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rxNumber);
        return newSet;
      });
      setEditingRow(null);

      notifications.show({
        title: "Success",
        message: "Order saved successfully",
        color: "green",
      });
    } catch (error) {
      console.error("Error saving order:", error);
      notifications.show({
        title: "Error",
        message: "Failed to save order",
        color: "red",
      });
    }
  };

  const handleCancelEdit = (rxNumber: string) => {
    // Reload data to revert changes
    fetchPendingOrders();
    setEditingRow(null);
    setModifiedRows((prev) => {
      const newSet = new Set(prev);
      newSet.delete(rxNumber);
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Filter orders based on current search
      const filtered = pendingOrders.filter(
        (d) =>
          (d.firstName + " " + d.lastName).toLowerCase().includes(searchText.toLowerCase()) ||
          d.dob?.includes(searchText.toLowerCase()) ||
          d.species?.toLowerCase().includes(searchText.toLowerCase()) ||
          d.medication?.toLowerCase().includes(searchText.toLowerCase()) ||
          d.form?.toLowerCase().includes(searchText.toLowerCase()) ||
          d.rxNumber?.toString().includes(searchText.toLowerCase()) ||
          d.memo?.toLowerCase().includes(searchText.toLowerCase())
      );
      const allRxNumbers = new Set(filtered.map((order) => order.rxNumber));
      setSelectedRows(allRxNumbers);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (rxNumber: string, checked: boolean) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rxNumber);
      } else {
        newSet.delete(rxNumber);
      }
      return newSet;
    });
  };

  const handleProcess = () => {
    // Filter only selected orders
    const selectedOrders = pendingOrders.filter((order) =>
      selectedRows.has(order.rxNumber)
    );

    if (selectedOrders.length === 0) {
      notifications.show({
        title: "No orders selected",
        message: "Please select at least one order to process",
        color: "orange",
      });
      return;
    }

    // Transform pending orders to batch preview format
    let batchPreviewData = selectedOrders.map((order) => ({
      firstName: order.firstName,
      lastName: order.lastName,
      dateOfBirth: new Date(order.dob),
      dobString: moment(order.dob).format("MM/DD/YYYY"),
      phone: formatPhoneNumber(order.cell), // Normalize phone number
      species: order.species.toUpperCase(),
      doctor: `${order.drFirstName} ${order.drLastName}`.trim(),
      med: order.medication,
      dosage_form: order.form,
      qty: order.qty || "0",
      price: order.price || "0",
      memo: order.memo || "",
      authRefills: order.authRefills || 0,
      rxNumber: order.rxNumber,
      orderStatus: "waiting-response",
      orderDate: order.orderDate ? new Date(order.orderDate) : new Date(),
      alt_qty: "",
      alt_price: "",
      metadata: {},
      state: order.patientAddress?.state || "", // Add state field for viewing in BatchPreview
    }));

    // Apply batch rules (same logic as BatchHistory.tsx)
    batchRules.forEach((rule) => {
      batchPreviewData = batchPreviewData.map((d) => {
        let ruleMatch = false;

        switch (rule.conditionType) {
          case BatchRuleConditionType.MedicationContains:
            ruleMatch = rule.conditionValue?.some((v) =>
              d.med.toLowerCase().includes(v.toLowerCase())
            );
            break;
          case BatchRuleConditionType.DosageForm:
            ruleMatch = rule.conditionValue?.some((v) =>
              d.dosage_form.toLowerCase().includes(v.toLowerCase())
            );
            break;
          case BatchRuleConditionType.Quantity:
            ruleMatch = rule.conditionValue?.some(
              (v) => d.qty === parseFloat(v)
            );
            break;
          case BatchRuleConditionType.DrLastName:
            ruleMatch = rule.conditionValue?.some((v) =>
              d.doctor.toLowerCase().includes(v.toLowerCase())
            );
            break;
          case BatchRuleConditionType.PriceExceeds:
            ruleMatch = rule.conditionValue?.some(
              (v) => parseFloat(String(d.price).replace("$", "")) > parseFloat(v)
            );
            break;
          case BatchRuleConditionType.DOB:
            ruleMatch = rule.conditionValue?.some((v) =>
              moment(d.dateOfBirth).isSame(moment(v, "MM/DD/YYYY"), "day")
            );
            break;
          case BatchRuleConditionType.SPECIE:
            ruleMatch = rule.conditionValue?.some((v) =>
              d.species.toLowerCase().includes(v.toLowerCase())
            );
            break;
          default:
            break;
        }

        if (ruleMatch) {
          if (rule.secondCondition) {
            let secondConditionMatch = false;
            switch (rule.secondCondition.key) {
              case BatchRuleConditionType.DosageForm:
                secondConditionMatch =
                  d.dosage_form === rule.secondCondition.value;
                break;
              case BatchRuleConditionType.MedicationContains:
                if (typeof rule.secondCondition.value === "string") {
                  secondConditionMatch = d.med
                    .toLowerCase()
                    .includes(rule.secondCondition.value.toLowerCase());
                }
                break;
              case BatchRuleConditionType.Quantity:
                secondConditionMatch =
                  d.qty === parseFloat(String(rule.secondCondition.value));
                break;
              case BatchRuleConditionType.DrLastName:
                if (typeof rule.secondCondition.value === "string") {
                  secondConditionMatch = d.doctor
                    .toLowerCase()
                    .includes(rule.secondCondition.value.toLowerCase());
                }
                break;
              case BatchRuleConditionType.PriceExceeds:
                if (typeof rule.secondCondition.value === "string") {
                  secondConditionMatch =
                    parseFloat(String(d.price).replace("$", "")) >
                    parseFloat(rule.secondCondition.value);
                }
                break;
              case BatchRuleConditionType.DOB:
                if (typeof rule.secondCondition.value === "string") {
                  secondConditionMatch = moment(d.dateOfBirth).isSame(
                    moment(rule.secondCondition.value, "MM/DD/YYYY"),
                    "day"
                  );
                }
                break;
              case BatchRuleConditionType.SPECIE:
                if (typeof rule.secondCondition.value === "string") {
                  secondConditionMatch =
                    d.species?.toLowerCase() ===
                    rule.secondCondition.value.toLowerCase();
                }
                break;
            }
            if (
              rule.secondCondition.logicalOp === "AND" &&
              !secondConditionMatch
            ) {
              ruleMatch = false;
            } else if (
              rule.secondCondition.logicalOp === "OR" &&
              secondConditionMatch
            ) {
              ruleMatch = true;
            }
          }
          if (ruleMatch) {
            d.metadata = {
              ...d.metadata,
              [rule.action.title]: true,
            };
          }
        }

        return d;
      });
    });

    // Parse memo field for auto-refill, alt_qty, alt_price
    batchPreviewData = batchPreviewData.map((item) => {
      if (item?.memo?.trim()) {
        let tmpMemo = item.memo?.toLowerCase();
        let splitMemo = tmpMemo.split(',');
        let status = null, alt_qnt = null, alt_price = null;
        if (splitMemo.length === 3) {
          [status, alt_qnt, alt_price] = splitMemo;
        } else if (splitMemo.length === 2) {
          [alt_qnt, alt_price] = splitMemo;
        } else if (splitMemo.length === 1) {
          [status] = splitMemo;
        }
        if (alt_qnt) {
          item.alt_qty = alt_qnt.trim();
        }
        if (alt_price) {
          item.alt_price = alt_price.trim();
        }
        if (status === "arf") {
          item.metadata = {
            ...item.metadata,
            "auto-refill": true
          };
        }
      }
      return item;
    });

    // Prepare metadata for archiving orders after batch upload
    const ordersToArchive = selectedOrders.map((order) => ({
      pendingOrdersId: order.pendingOrdersId,
      rxNumber: order.rxNumber,
    }));

    // Navigate to BatchPreview with the transformed data
    navigate(ROUTES.BatchPreview.path, {
      state: {
        data: batchPreviewData,
        update: false,
        ordersToArchive: ordersToArchive,
        fromProcessPending: true, // Flag to indicate data source
      },
    });
  };

  const handleDownloadCSV = () => {
    // Filter only selected orders
    const selectedOrders = pendingOrders.filter((order) =>
      selectedRows.has(order.rxNumber)
    );

    if (selectedOrders.length === 0) {
      notifications.show({
        title: "No orders selected",
        message: "Please select at least one order to download",
        color: "orange",
      });
      return;
    }

    // Transform to CSV format matching Preliminary_-_Rx2_Report.csv
    const csvData = selectedOrders.map((order) => {
      // Format dates to MM/DD/YYYY
      let orderDate = order.orderDate;
      if (orderDate) {
        orderDate = moment(orderDate).format("MM/DD/YYYY");
      } else {
        orderDate = moment().format("MM/DD/YYYY");
      }

      // Format DOB to MM/DD/YYYY
      let dob = order.dob;
      if (dob) {
        // Parse various date formats and convert to MM/DD/YYYY
        dob = moment(dob, ["YYYY-MM-DD", "MM-DD-YYYY", "MM/DD/YYYY", "MM-DD-YY", "DD-MM-YYYY"]).format("MM/DD/YYYY");
      }

      return {
        "Rx Number": order.rxNumber,
        "Order Date": orderDate,
        "Last Name": order.lastName,
        "First Name": order.firstName,
        "Species": order.species.toUpperCase(),
        "DOB": dob,
        "Cell": order.cell,
        "Dr Last Name": order.drLastName,
        "Dr First Name": order.drFirstName,
        "Medication": order.medication,
        "Form": order.form,
        "Qty": order.qty,
        "Day supply": order.daySupply || "",
        "Price": order.price,
        "Order Status": "Rx2Text Submitted",
        "Memo": order.memo || "",
        "Auth. Refills": order.authRefills || 0,
      };
    });

    // Convert to CSV
    const csv = Papa.unparse(csvData);

    // Create download link
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `pending_orders_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notifications.show({
      title: "Success",
      message: `Downloaded ${selectedOrders.length} orders as CSV`,
      color: "green",
    });
  };

  const searchFilter = (data: any[], text: string) =>
    data.filter(
      (d) =>
        (d.firstName + " " + d.lastName).toLowerCase().includes(text.toLowerCase()) ||
        d.dob?.includes(text.toLowerCase()) ||
        d.species?.toLowerCase().includes(text.toLowerCase()) ||
        d.medication?.toLowerCase().includes(text.toLowerCase()) ||
        d.form?.toLowerCase().includes(text.toLowerCase()) ||
        d.rxNumber?.toString().includes(text.toLowerCase()) ||
        d.memo?.toLowerCase().includes(text.toLowerCase())
    );

  let filteredOrders = useMemo(
    () => searchFilter(pendingOrders, searchText),
    [pendingOrders, searchText]
  );

  const paginatedOrders = useMemo(() => {
    const size = parseInt(pendingPageSize);
    const start = (pendingPage - 1) * size;
    const end = start + size;
    return filteredOrders.slice(start, end);
  }, [filteredOrders, pendingPage, pendingPageSize]);

  const totalPendingPages = searchText.trim()
    ? Math.ceil(filteredOrders.length / parseInt(pendingPageSize))
    : Math.ceil(totalPendingCount / parseInt(pendingPageSize));

  // Validate a single order and return validation errors
  const validateOrder = (order: any): string[] => {
    const errors: string[] = [];
    
    if (!order.form || order.form.trim() === "") {
      errors.push("Form is empty");
    }
    
    if (!order.price || order.price.toString().trim() === "" || parseFloat(order.price) === 0) {
      errors.push("Price is 0 or empty");
    }
    
    if (!order.cell || order.cell.toString().trim() === "" || order.cell === "0") {
      errors.push("Cell is empty or 0");
    }
    
    return errors;
  };

  // Check if any selected orders have invalid form, price, or cell
  const hasInvalidSelectedOrders = useMemo(() => {
    const selectedOrders = pendingOrders.filter((order) =>
      selectedRows.has(order.rxNumber)
    );

    return selectedOrders.some((order) => {
      const errors = validateOrder(order);
      return errors.length > 0;
    });
  }, [pendingOrders, selectedRows]);

  const rows = paginatedOrders.map((order: any) => {
    const isEditing = editingRow === order.rxNumber;
    const validationErrors = validateOrder(order);
    const isInvalid = validationErrors.length > 0;
    const errorMessage = validationErrors.join(", ");

    return (
    <Tooltip
      label={errorMessage}
      disabled={!isInvalid}
      position="top"
      multiline
      w={300}
      withArrow
    >
      <Table.Tr 
        key={order.rxNumber}
        style={{ 
          backgroundColor: isInvalid ? "#ffe5e5" : "transparent",
          cursor: isInvalid ? "help" : "default"
        }}
      >
      <Table.Td>
        <Checkbox
          checked={selectedRows.has(order.rxNumber)}
          onChange={(e) => handleSelectRow(order.rxNumber, e.currentTarget.checked)}
        />
      </Table.Td>
      <Table.Td>
        {process.env.REACT_APP_ENV === "dev" && isEditing ? (
          <TextInput
            size="sm"
            value={order.rxNumber}
            name="rxNumber"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            w={90}
          />
        ) : (
          <Text size="sm">{order.rxNumber}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.firstName}
            name="firstName"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
          />
        ) : (
          <Text size="sm">{order.firstName}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.lastName}
            name="lastName"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
          />
        ) : (
          <Text size="sm">{order.lastName}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.species}
            name="species"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
          />
        ) : (
          <Text size="sm">{order.species}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <Textarea
            size="sm"
            value={order.dob}
            name="dob"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            placeholder="yyyy-mm-dd"
            autosize
            minRows={1}
            w={115}
          />
        ) : (
          <Text size="sm">{order.dob}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <Textarea
            size="sm"
            value={order.cell}
            name="cell"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            autosize
            minRows={1}
            w={110}
          />
        ) : (
          <Text size="sm">{order.cell}</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.patientAddress?.state ? order.patientAddress.state : "-"}</Text>
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <Flex direction="column" gap={4}>
            <Textarea
              size="sm"
              value={order.drFirstName}
              name="drFirstName"
              onChange={(e) => handleValueChange(e, order.rxNumber)}
              placeholder="First"
              autosize
              minRows={1}
            />
            <Textarea
              size="sm"
              value={order.drLastName}
              name="drLastName"
              onChange={(e) => handleValueChange(e, order.rxNumber)}
              placeholder="Last"
              autosize
              minRows={1}
            />
          </Flex>
        ) : (
          <Text size="sm">{`${order.drFirstName} ${order.drLastName}`}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <Textarea
            size="sm"
            value={order.medication}
            name="medication"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            autosize
            minRows={1}
          />
        ) : (
          <Text size="sm">{order.medication}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <Textarea
            size="sm"
            value={order.form}
            name="form"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            autosize
            minRows={1}
          />
        ) : (
          <Text size="sm">{order.form}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.qty}
            name="qty"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            w={60}
          />
        ) : (
          <Text size="sm">{order.qty}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.price}
            name="price"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            w={70}
          />
        ) : (
          <Text size="sm">{order.price}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.daySupply || ""}
            name="daySupply"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            w={60}
          />
        ) : (
          <Text size="sm">{order.daySupply || ""}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <TextInput
            size="sm"
            value={order.authRefills || ""}
            name="authRefills"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            w={50}
          />
        ) : (
          <Text size="sm">{order.authRefills || ""}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {isEditing ? (
          <Textarea
            size="sm"
            value={order.memo || ""}
            name="memo"
            onChange={(e) => handleValueChange(e, order.rxNumber)}
            autosize
            minRows={1}
          />
        ) : (
          <Text size="sm">{order.memo || ""}</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Flex gap={8} wrap="wrap">
          {isEditing ? (
            <>
              <Button
                size="xs"
                color="green"
                variant="filled"
                onClick={() => handleSaveRow(order.pendingOrdersId, order.rxNumber)}
                disabled={!modifiedRows.has(order.rxNumber)}
              >
                Save
              </Button>
              <Button
                size="xs"
                color="gray"
                variant="outline"
                onClick={() => handleCancelEdit(order.rxNumber)}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                size="xs"
                color="blue"
                variant="light"
                onClick={() => setEditingRow(order.rxNumber)}
              >
                Edit
              </Button>
              <Button
                size="xs"
                color="red"
                variant="outline"
                onClick={() => handleArchive(order.pendingOrdersId, order.rxNumber)}
              >
                Archive
              </Button>
            </>
          )}
        </Flex>
      </Table.Td>
    </Table.Tr>
    </Tooltip>
    );
  });

  let filteredArchivedOrders = useMemo(
    () => searchFilter(archivedOrders, searchText),
    [archivedOrders, searchText]
  );

  const paginatedArchivedOrders = useMemo(() => {
    const size = parseInt(archivedPageSize);
    const start = (archivedPage - 1) * size;
    const end = start + size;
    return filteredArchivedOrders.slice(start, end);
  }, [filteredArchivedOrders, archivedPage, archivedPageSize]);

  const totalArchivedPages = searchText.trim()
    ? Math.ceil(filteredArchivedOrders.length / parseInt(archivedPageSize))
    : Math.ceil(totalArchivedCount / parseInt(archivedPageSize));

  const archivedRows = paginatedArchivedOrders.map((order: any) => (
    <Table.Tr key={order.rxNumber}>
      <Table.Td>
        <Text size="sm">{order.rxNumber}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.firstName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.lastName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.species}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.dob}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.cell}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.patientAddress?.state ? order.patientAddress.state : "-"}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{`${order.drFirstName} ${order.drLastName}`}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.medication}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.form}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.qty}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.price}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.daySupply || ""}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.authRefills || ""}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{order.memo || ""}</Text>
      </Table.Td>
      <Table.Td>
        <Button
          size="xs"
          color="green"
          variant="outline"
          onClick={() => handleUnarchive(order.pendingOrdersId, order.rxNumber)}
        >
          Unarchive
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Flex direction={"column"} gap={15} p={20}>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="pending">Process Pending</Tabs.Tab>
          <Tabs.Tab value="archived">Archived</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="pending" pt={15}>
          <Flex direction={"column"} gap={15} p={20} bg="white" style={{ borderRadius: 8 }}>
            <TextInput
              placeholder="Search by name, DOB, species, medication, form, memo, or RX number..."
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
              size="md"
            />
            <Flex gap={10} justify={"flex-end"} mb={15}>
              <Button
                onClick={toggleSidebar}
                radius={"md"}
                size="sm"
                variant="outline">
                {sidebarCollapsed ? "Show Sidebar" : "Collapse Sidebar"}
              </Button>
              <Tooltip
                label={
                  modifiedRows.size > 0
                    ? `Please save ${modifiedRows.size} unsaved row(s) before processing batch`
                    : selectedRows.size === 0
                    ? "Please select at least one order"
                    : hasInvalidSelectedOrders
                    ? "Some selected orders have empty/invalid form, cell, or price (0 or empty)"
                    : ""
                }
                disabled={selectedRows.size > 0 && modifiedRows.size === 0 && !hasInvalidSelectedOrders}
                position="bottom"
                withArrow>
                <Button
                  loading={processing}
                  onClick={handleProcess}
                  disabled={selectedRows.size === 0 || modifiedRows.size > 0 || hasInvalidSelectedOrders}
                  radius={"md"}
                  size="sm">
                  Process Batch ({selectedRows.size} selected)
                </Button>
              </Tooltip>
              <Button
                onClick={handleDownloadCSV}
                disabled={selectedRows.size === 0}
                radius={"md"}
                size="sm"
                variant="outline">
                Download CSV ({selectedRows.size} selected)
              </Button>
              <Button
                loading={processing}
                onClick={handleBulkArchive}
                disabled={selectedRows.size === 0}
                radius={"md"}
                size="sm"
                color="red"
                variant="outline">
                Bulk Archive ({selectedRows.size} selected)
              </Button>
            </Flex>

            <Flex mah={"65vh"} style={{ overflowY: "auto", overflowX: "auto" }}>
              <LoadingOverlay visible={pendingLoading} />
              <Table highlightOnHover withTableBorder withColumnBorders style={{ width: "100%" }}>
                <Table.Thead
                  bg={colors.bg}
                  style={{ position: "sticky", top: 0, zIndex: 999 }}>
                  <Table.Tr>
                    <Table.Th>
                      <Checkbox
                        checked={filteredOrders.length > 0 && selectedRows.size === filteredOrders.length}
                        indeterminate={selectedRows.size > 0 && selectedRows.size < filteredOrders.length}
                        onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                      />
                    </Table.Th>
                    <Table.Th>RX NUMBER</Table.Th>
                    <Table.Th>FIRST NAME</Table.Th>
                    <Table.Th>LAST NAME</Table.Th>
                    <Table.Th>SPECIES</Table.Th>
                    <Table.Th>DOB</Table.Th>
                    <Table.Th style={{ width: 120 }}>CELL</Table.Th>
                    <Table.Th>STATE</Table.Th>
                    <Table.Th>DOCTOR</Table.Th>
                    <Table.Th>MEDICATION</Table.Th>
                    <Table.Th>FORM</Table.Th>
                    <Table.Th>QTY</Table.Th>
                    <Table.Th>PRICE</Table.Th>
                    <Table.Th>DAY SUPPLY</Table.Th>
                    <Table.Th>REFILLS</Table.Th>
                    <Table.Th>MEMO</Table.Th>
                    <Table.Th>ACTION</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody bg={"white"}>
                  {rows.length > 0 ? (
                    rows
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={17} style={{ textAlign: "center", padding: "2rem" }}>
                        <Text size="md" c="dimmed">No data found</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Flex>

            <Flex justify="space-between" align="center" mt={15}>
              <Flex gap={10} align="center">
                <Text size="sm">
                  Showing {filteredOrders.length === 0 ? 0 : (pendingPage - 1) * parseInt(pendingPageSize) + 1} to{" "}
                  {Math.min(pendingPage * parseInt(pendingPageSize), filteredOrders.length)} of {filteredOrders.length} entries
                </Text>
                <Select
                  value={pendingPageSize}
                  onChange={(value) => {
                    setPendingPageSize(value || "50");
                    setPendingPage(1);
                  }}
                  data={[
                    { value: "50", label: "50 per page" },
                    { value: "100", label: "100 per page" },
                    { value: "150", label: "150 per page" },
                    { value: "200", label: "200 per page" },
                  ]}
                  size="sm"
                  w={150}
                />
              </Flex>
              <Pagination
                value={pendingPage}
                onChange={setPendingPage}
                total={totalPendingPages}
                siblings={1}
                boundaries={1}
              />
            </Flex>
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="archived" pt={15}>
          <Flex direction={"column"} gap={15} p={20} bg="white" style={{ borderRadius: 8 }}>
            <TextInput
              placeholder="Search by name, DOB, species, medication, form, memo, or RX number..."
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
              size="md"
            />
            <Flex mah={"65vh"} style={{ overflowY: "auto", overflowX: "auto" }}>
              <LoadingOverlay visible={archivedLoading} />
              <Table highlightOnHover withTableBorder withColumnBorders style={{ width: "100%" }}>
                <Table.Thead
                  bg={colors.bg}
                  style={{ position: "sticky", top: 0, zIndex: 999 }}>
                  <Table.Tr>
                    <Table.Th>RX NUMBER</Table.Th>
                    <Table.Th>FIRST NAME</Table.Th>
                    <Table.Th>LAST NAME</Table.Th>
                    <Table.Th>SPECIES</Table.Th>
                    <Table.Th>DOB</Table.Th>
                    <Table.Th style={{ width: 120 }}>CELL</Table.Th>
                    <Table.Th>STATE</Table.Th>
                    <Table.Th>DOCTOR</Table.Th>
                    <Table.Th>MEDICATION</Table.Th>
                    <Table.Th>FORM</Table.Th>
                    <Table.Th>QTY</Table.Th>
                    <Table.Th>PRICE</Table.Th>
                    <Table.Th>DAY SUPPLY</Table.Th>
                    <Table.Th>REFILLS</Table.Th>
                    <Table.Th>MEMO</Table.Th>
                    <Table.Th>ACTION</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody bg={"white"}>
                  {archivedRows.length > 0 ? (
                    archivedRows
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: "center", padding: "2rem" }}>
                        <Text size="md" c="dimmed">No data found</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Flex>

            <Flex justify="space-between" align="center" mt={15}>
              <Flex gap={10} align="center">
                <Text size="sm">
                  Showing {filteredArchivedOrders.length === 0 ? 0 : (archivedPage - 1) * parseInt(archivedPageSize) + 1} to{" "}
                  {Math.min(archivedPage * parseInt(archivedPageSize), filteredArchivedOrders.length)} of {filteredArchivedOrders.length} entries
                </Text>
                <Select
                  value={archivedPageSize}
                  onChange={(value) => {
                    setArchivedPageSize(value || "50");
                    setArchivedPage(1);
                  }}
                  data={[
                    { value: "50", label: "50 per page" },
                    { value: "100", label: "100 per page" },
                    { value: "150", label: "150 per page" },
                    { value: "200", label: "200 per page" },
                  ]}
                  size="sm"
                  w={150}
                />
              </Flex>
              <Pagination
                value={archivedPage}
                onChange={setArchivedPage}
                total={totalArchivedPages}
                siblings={1}
                boundaries={1}
              />
            </Flex>
          </Flex>
        </Tabs.Panel>
      </Tabs>
    </Flex>
  );
}

export default ProcessPending;
